import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Card, Alert, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { workoutAPI } from '../services/workoutAPI';
import SetLogger from './SetLogger';
import CardioLogger from './CardioLogger';
import RestBlockCard from './RestBlockCard';
import ExerciseForm from './ExerciseForm';

function TemplateForm({ show, onHide, template, onSave, error }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    notes: '',
    exercises: [] // Array of items (exercises, supersets, rest blocks)
  });

  const [errors, setErrors] = useState({});
  const [availableExercises, setAvailableExercises] = useState([]);
  const [isLoadingExercises, setIsLoadingExercises] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);

  // Exercise creation modal
  const [showExerciseForm, setShowExerciseForm] = useState(false);
  const [isSavingExercise, setIsSavingExercise] = useState(false);

  // Superset creation
  const [selectedExerciseIds, setSelectedExerciseIds] = useState(new Set());
  const [showSupersetModal, setShowSupersetModal] = useState(false);

  // Drag and drop
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    if (show) {
      fetchExercises();
    }
  }, [show]);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        description: template.description || '',
        notes: template.notes || '',
        exercises: (template.exercises || []).map((ex, index) => {
          const item = {
            ...ex,
            type: ex.item_type || 'exercise',
            id: Date.now() + index
          };

          // Handle rest blocks
          if (ex.item_type === 'rest_block') {
            item.rest_duration_seconds = ex.rest_duration_seconds || 60;
            item.rest_notes = ex.rest_notes || ex.notes || '';
          } else {
            // Handle exercises
            item.notes = ex.notes || '';
            // Convert null values to empty strings in sets
            item.sets = ex.sets ? ex.sets.map(set => ({
              ...set,
              weight: set.weight || '',
              reps: set.reps || '',
              duration_seconds: set.duration_seconds || '',
              notes: set.notes || ''
            })) : (ex.exercise_type === 'weight_reps' || ex.exercise_type === 'weight_time' ? [{
              set_number: 1,
              weight: '',
              weight_unit: 'lbs',
              reps: ex.exercise_type === 'weight_reps' ? '' : undefined,
              duration_seconds: ex.exercise_type === 'weight_time' ? '' : undefined,
              notes: ''
            }] : []);
            item.cardio_data = ex.cardio_data || null;
            item.cardio_segments = ex.cardio_segments || [];
          }

          return item;
        })
      });
    } else {
      setFormData({
        name: '',
        description: '',
        notes: '',
        exercises: []
      });
    }
    setErrors({});
    setSelectedExerciseIds(new Set());
  }, [template, show]);

  const fetchExercises = async () => {
    setIsLoadingExercises(true);
    try {
      const data = await workoutAPI.fetchExercises(false);
      setAvailableExercises(data);
    } catch (err) {
      console.error('Error fetching exercises:', err);
    } finally {
      setIsLoadingExercises(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAddExercise = () => {
    if (!selectedExercise) return;

    const usesSets = selectedExercise.exercise_type === 'weight_reps' ||
                     selectedExercise.exercise_type === 'weight_time';

    const newExercise = {
      type: 'exercise',
      item_type: 'exercise',
      id: Date.now(),
      exercise_id: selectedExercise.value,
      exercise_name: selectedExercise.label,
      exercise_type: selectedExercise.exercise_type,
      category: selectedExercise.category,
      notes: '',
      sets: usesSets ? [{
        set_number: 1,
        weight: '',
        weight_unit: 'lbs',
        reps: selectedExercise.exercise_type === 'weight_reps' ? '' : undefined,
        duration_seconds: selectedExercise.exercise_type === 'weight_time' ? '' : undefined,
        notes: ''
      }] : [],
      cardio_data: null,
      cardio_segments: []
    };

    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, newExercise]
    }));
    setSelectedExercise(null);
  };

  const handleAddRestBlock = () => {
    const newRestBlock = {
      type: 'rest_block',
      item_type: 'rest_block',
      id: Date.now(),
      rest_duration_seconds: 60,
      rest_notes: ''
    };

    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, newRestBlock]
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== index)
    }));
    // Clear any selection
    setSelectedExerciseIds(new Set());
  };

  const handleUpdateExerciseSets = (index, data) => {
    const newExercises = [...formData.exercises];
    newExercises[index].sets = data.sets;
    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));
  };

  const handleUpdateExerciseCardio = (index, data) => {
    const newExercises = [...formData.exercises];
    if (data.cardio_data !== undefined) {
      newExercises[index].cardio_data = data.cardio_data;
    }
    if (data.cardio_segments !== undefined) {
      newExercises[index].cardio_segments = data.cardio_segments;
    }
    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));
  };

  const handleUpdateExerciseNotes = (index, notes) => {
    const newExercises = [...formData.exercises];
    newExercises[index].notes = notes;
    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newExercises = [...formData.exercises];
    const draggedItem = newExercises[draggedIndex];

    // Check if dragging a superset - need to move all exercises in the superset together
    if (draggedItem.superset_group_id) {
      const supersetGroupId = draggedItem.superset_group_id;
      const supersetExercises = newExercises.filter(ex => ex.superset_group_id === supersetGroupId);
      const nonSupersetExercises = newExercises.filter(ex => ex.superset_group_id !== supersetGroupId);

      // Find the position to insert
      const dropItem = newExercises[dropIndex];
      let insertIndex;

      if (dropItem.superset_group_id === supersetGroupId) {
        // Dropping on itself, do nothing
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }

      // Find position in non-superset array
      insertIndex = nonSupersetExercises.findIndex(ex =>
        newExercises.indexOf(ex) === dropIndex
      );

      if (insertIndex === -1) {
        insertIndex = nonSupersetExercises.length;
      }

      // Insert superset exercises at the new position
      nonSupersetExercises.splice(insertIndex, 0, ...supersetExercises);

      setFormData(prev => ({
        ...prev,
        exercises: nonSupersetExercises
      }));
    } else {
      // Single item drag
      newExercises.splice(draggedIndex, 1);
      newExercises.splice(dropIndex, 0, draggedItem);

      setFormData(prev => ({
        ...prev,
        exercises: newExercises
      }));
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Superset functionality
  const handleToggleExerciseSelection = (index) => {
    const newSelected = new Set(selectedExerciseIds);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedExerciseIds(newSelected);
  };

  const handleCreateSuperset = () => {
    if (selectedExerciseIds.size < 2) {
      alert('Select at least 2 exercises to create a superset');
      return;
    }

    const selectedIndices = Array.from(selectedExerciseIds).sort((a, b) => a - b);
    const supersetGroupId = Date.now();

    const newExercises = [...formData.exercises];
    selectedIndices.forEach(index => {
      newExercises[index].superset_group_id = supersetGroupId;
      newExercises[index].item_type = 'superset';
    });

    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));

    setSelectedExerciseIds(new Set());
  };

  const handleBreakSuperset = (supersetGroupId) => {
    const newExercises = formData.exercises.map(ex => {
      if (ex.superset_group_id === supersetGroupId) {
        return {
          ...ex,
          superset_group_id: null,
          item_type: 'exercise'
        };
      }
      return ex;
    });

    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));
  };

  // Create new exercise
  const handleCreateExercise = async (exerciseData) => {
    setIsSavingExercise(true);
    try {
      const newExercise = await workoutAPI.createExercise(exerciseData);
      await fetchExercises();
      setShowExerciseForm(false);
      // Auto-select the new exercise
      const exerciseOption = {
        value: newExercise.id,
        label: newExercise.name,
        exercise_type: newExercise.exercise_type,
        category: newExercise.category
      };
      setSelectedExercise(exerciseOption);
    } catch (err) {
      console.error('Error creating exercise:', err);
    } finally {
      setIsSavingExercise(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (formData.exercises.length === 0) {
      newErrors.exercises = 'Add at least one exercise or rest block to the template';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSaving(true);
    try {
      // Format exercises for backend
      const formattedExercises = formData.exercises.map((item, index) => {
        const formatted = {
          item_type: item.item_type || item.type || 'exercise',
          sort_order: index
        };

        if (item.item_type === 'rest_block' || item.type === 'rest_block') {
          // Rest block
          formatted.rest_duration_seconds = item.rest_duration_seconds || 60;
          formatted.notes = item.rest_notes || '';
        } else {
          // Exercise
          formatted.exercise_id = item.exercise_id;
          formatted.superset_group_id = item.superset_group_id || null;
          formatted.notes = item.notes || '';
          formatted.sets = item.sets || [];
          formatted.cardio_data = item.cardio_data;
          formatted.cardio_segments = item.cardio_segments || [];
        }

        return formatted;
      });

      const dataToSave = {
        name: formData.name,
        description: formData.description,
        notes: formData.notes,
        exercises: formattedExercises
      };

      await onSave(dataToSave);
    } catch (err) {
      console.error('Error saving template:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Prepare exercise options for select dropdown
  const exerciseOptions = availableExercises.map(ex => ({
    value: ex.id,
    label: ex.name,
    exercise_type: ex.exercise_type,
    category: ex.category
  }));

  // Group exercises by superset
  const groupedExercises = [];
  const processedSupersets = new Set();

  formData.exercises.forEach((item, index) => {
    if (item.superset_group_id && !processedSupersets.has(item.superset_group_id)) {
      // Find all exercises in this superset
      const supersetExercises = formData.exercises
        .map((ex, idx) => ({ ...ex, originalIndex: idx }))
        .filter(ex => ex.superset_group_id === item.superset_group_id);

      groupedExercises.push({
        type: 'superset',
        id: item.superset_group_id,
        exercises: supersetExercises
      });

      processedSupersets.add(item.superset_group_id);
    } else if (!item.superset_group_id) {
      groupedExercises.push({
        type: item.item_type || item.type || 'exercise',
        ...item,
        originalIndex: index
      });
    }
  });

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-file-alt me-2"></i>
            {template ? 'Edit Template' : 'Create New Template'}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body style={{ maxHeight: '70vh' }}>
            {error && (
              <Alert variant="danger">
                {error}
              </Alert>
            )}

            {/* Template Name */}
            <Form.Group className="mb-3">
              <Form.Label>
                Template Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                isInvalid={!!errors.name}
                placeholder="e.g., Upper Body Push, Leg Day, Full Body"
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Description */}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of this workout template"
              />
            </Form.Group>

            {/* Add Exercises Toolbar */}
            <div className="mb-3 p-3 bg-light rounded">
              <div className="row g-2">
                <div className="col-md-6">
                  <Select
                    options={exerciseOptions}
                    onChange={setSelectedExercise}
                    value={selectedExercise}
                    isLoading={isLoadingExercises}
                    placeholder="Select an exercise to add..."
                    isClearable
                  />
                </div>
                <div className="col-md-6 d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleAddExercise}
                    disabled={!selectedExercise}
                  >
                    <i className="fas fa-plus me-1"></i>
                    Add Exercise
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowExerciseForm(true)}
                  >
                    <i className="fas fa-plus-circle me-1"></i>
                    New Exercise
                  </Button>
                  <Button
                    variant="warning"
                    onClick={handleAddRestBlock}
                  >
                    <i className="fas fa-pause me-1"></i>
                    Add Rest
                  </Button>
                </div>
              </div>

              {/* Superset controls */}
              {selectedExerciseIds.size > 0 && (
                <div className="mt-2">
                  <Badge bg="info" className="me-2">
                    {selectedExerciseIds.size} selected
                  </Badge>
                  <Button
                    variant="info"
                    size="sm"
                    onClick={handleCreateSuperset}
                    disabled={selectedExerciseIds.size < 2}
                  >
                    <i className="fas fa-link me-1"></i>
                    Create Superset
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ms-2"
                    onClick={() => setSelectedExerciseIds(new Set())}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}

              {errors.exercises && (
                <div className="text-danger small mt-2">{errors.exercises}</div>
              )}
            </div>

            {/* Exercises List */}
            <div className="template-exercises-list">
              {groupedExercises.map((item, groupIndex) => {
                if (item.type === 'superset') {
                  // Use first exercise's index for dragging
                  const firstExerciseIndex = item.exercises[0].originalIndex;
                  return (
                    <Card
                      key={item.id}
                      className={`mb-3 superset-card ${draggedIndex === firstExerciseIndex ? 'dragging' : ''} ${dragOverIndex === firstExerciseIndex ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, firstExerciseIndex)}
                      onDragOver={(e) => handleDragOver(e, firstExerciseIndex)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, firstExerciseIndex)}
                    >
                      <Card.Header className="bg-info text-white">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <span className="drag-handle me-2">
                              <i className="fas fa-grip-vertical"></i>
                            </span>
                            <h6 className="mb-0">
                              <i className="fas fa-link me-2"></i>
                              Superset ({item.exercises.length} exercises)
                            </h6>
                          </div>
                          <Button
                            variant="outline-light"
                            size="sm"
                            onClick={() => handleBreakSuperset(item.id)}
                          >
                            <i className="fas fa-unlink me-1"></i>
                            Break Superset
                          </Button>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {item.exercises.map((ex) => (
                          <div key={ex.originalIndex} className="superset-exercise mb-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <strong>{ex.exercise_name}</strong>
                                <Badge bg="primary" className="ms-2">{ex.exercise_type}</Badge>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveItem(ex.originalIndex)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </div>

                            {/* Set logger for weight exercises */}
                            {(ex.exercise_type === 'weight_reps' || ex.exercise_type === 'weight_time') && (
                              <SetLogger
                                exercise={ex}
                                onUpdate={(data) => handleUpdateExerciseSets(ex.originalIndex, data)}
                              />
                            )}

                            {/* Cardio logger */}
                            {(ex.exercise_type === 'cardio_basic' || ex.exercise_type === 'cardio_segments') && (
                              <CardioLogger
                                exercise={ex}
                                onUpdate={(data) => handleUpdateExerciseCardio(ex.originalIndex, data)}
                              />
                            )}

                            <Form.Group className="mt-2">
                              <Form.Control
                                as="textarea"
                                rows={1}
                                size="sm"
                                placeholder="Exercise notes (optional)"
                                value={ex.notes || ''}
                                onChange={(e) => handleUpdateExerciseNotes(ex.originalIndex, e.target.value)}
                              />
                            </Form.Group>
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  );
                } else if (item.type === 'rest_block' || item.item_type === 'rest_block') {
                  return (
                    <div
                      key={item.originalIndex}
                      className={`mb-3 ${draggedIndex === item.originalIndex ? 'dragging' : ''} ${dragOverIndex === item.originalIndex ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item.originalIndex)}
                      onDragOver={(e) => handleDragOver(e, item.originalIndex)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, item.originalIndex)}
                    >
                      <RestBlockCard
                        restBlock={item}
                        onUpdate={(updatedData) => {
                          const newExercises = [...formData.exercises];
                          newExercises[item.originalIndex] = {
                            ...newExercises[item.originalIndex],
                            ...updatedData
                          };
                          setFormData(prev => ({
                            ...prev,
                            exercises: newExercises
                          }));
                        }}
                        onDelete={() => handleRemoveItem(item.originalIndex)}
                      />
                    </div>
                  );
                } else {
                  // Regular exercise
                  return (
                    <Card
                      key={item.originalIndex}
                      className={`mb-3 exercise-card ${draggedIndex === item.originalIndex ? 'dragging' : ''} ${dragOverIndex === item.originalIndex ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item.originalIndex)}
                      onDragOver={(e) => handleDragOver(e, item.originalIndex)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, item.originalIndex)}
                    >
                      <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <span className="drag-handle me-2">
                              <i className="fas fa-grip-vertical"></i>
                            </span>
                            <Form.Check
                              type="checkbox"
                              className="me-2"
                              checked={selectedExerciseIds.has(item.originalIndex)}
                              onChange={() => handleToggleExerciseSelection(item.originalIndex)}
                            />
                            <strong>{item.exercise_name}</strong>
                            <Badge bg="primary" className="ms-2">{item.exercise_type}</Badge>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveItem(item.originalIndex)}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {/* Set logger for weight exercises */}
                        {(item.exercise_type === 'weight_reps' || item.exercise_type === 'weight_time') && (
                          <SetLogger
                            exercise={item}
                            onUpdate={(data) => handleUpdateExerciseSets(item.originalIndex, data)}
                          />
                        )}

                        {/* Cardio logger */}
                        {(item.exercise_type === 'cardio_basic' || item.exercise_type === 'cardio_segments') && (
                          <CardioLogger
                            exercise={item}
                            onUpdate={(data) => handleUpdateExerciseCardio(item.originalIndex, data)}
                          />
                        )}

                        <Form.Group className="mt-2">
                          <Form.Control
                            as="textarea"
                            rows={1}
                            size="sm"
                            placeholder="Exercise notes (optional)"
                            value={item.notes || ''}
                            onChange={(e) => handleUpdateExerciseNotes(item.originalIndex, e.target.value)}
                          />
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  );
                }
              })}

              {formData.exercises.length === 0 && (
                <Card className="text-center py-5 bg-light">
                  <Card.Body>
                    <i className="fas fa-dumbbell fa-3x text-muted mb-3"></i>
                    <p className="text-muted">
                      No exercises added yet. Add exercises above to build your template.
                    </p>
                  </Card.Body>
                </Card>
              )}
            </div>

            {/* Template Notes */}
            <Form.Group className="mt-3">
              <Form.Label>Template Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="General notes about this template"
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onHide} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Save Template
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Exercise Creation Modal */}
      {showExerciseForm && (
        <ExerciseForm
          show={showExerciseForm}
          onHide={() => setShowExerciseForm(false)}
          exercise={null}
          onSave={handleCreateExercise}
          isLoading={isSavingExercise}
        />
      )}
    </>
  );
}

export default TemplateForm;
